import { createApp } from 'vue'
import Antd from 'ant-design-vue'
import { createPinia } from 'pinia'

import './style/index.less'
import './style/fix-body-leak.less'
import i18n from './locales'

// ===== 彻底根治 body width:calc 白边（必须同步执行，早于 Antd 弹窗） =====
;(() => {
  const isDesktopActive = () => {
    try {
      return typeof document !== 'undefined' && document.body.classList.contains('pc-desktop-runtime-active')
    } catch {
      return false
    }
  }
  const isCalcWidth = (v) => typeof v === 'string' && v.includes('calc(100% -')
  const hasVisibleOverlay = () => {
    try {
      if (document.querySelector('.ant-modal-mask[style*="display: block"], .ant-drawer-mask[style*="display: block"]')) return true
      const wraps = document.querySelectorAll('.ant-modal-wrap')
      for (const w of wraps) {
        const cs = window.getComputedStyle(w)
        if (cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) !== 0) return true
      }
      if (document.querySelector('.ant-drawer-open')) return true
      return false
    } catch {
      return false
    }
  }
  const forceCleanup = () => {
    try {
      const body = document.body
      if (!body) return
      const w = body.style.width
      const pr = body.style.paddingRight
      if (isCalcWidth(w) && (isDesktopActive() || !hasVisibleOverlay())) body.style.width = ''
      if (isCalcWidth(pr) && (isDesktopActive() || !hasVisibleOverlay())) body.style.paddingRight = ''
      if (isDesktopActive() && isCalcWidth(body.style.width)) body.style.width = ''
      if (!hasVisibleOverlay() && body.classList.contains('ant-scrolling-effect') && !isDesktopActive()) {
        body.classList.remove('ant-scrolling-effect')
        if (isCalcWidth(body.style.width)) body.style.width = ''
        if (body.style.overflow === 'hidden') body.style.overflow = ''
      }
      // 桌面态固定列无需偏移
      if (isDesktopActive()) {
        try {
          document.querySelectorAll('.ant-table-cell-fix-right[style*="right:"]').forEach((el) => {
            const r = el.style.right
            if (r && r !== '0px' && r !== '0') el.style.right = '0px'
          })
        } catch {}
      }
    } catch {}
  }
  // 原型级拦截（Chrome/Firefox 对 style.width 的实现不同，需双重保障）
  try {
    if (typeof CSSStyleDeclaration !== 'undefined' && CSSStyleDeclaration.prototype) {
      const proto = CSSStyleDeclaration.prototype
      const widthDesc = Object.getOwnPropertyDescriptor(proto, 'width')
      const prDesc = Object.getOwnPropertyDescriptor(proto, 'paddingRight')
      if (widthDesc && widthDesc.set) {
        const origSet = widthDesc.set
        Object.defineProperty(proto, 'width', {
          get: widthDesc.get,
          set(value) {
            const isBody = this === document.body.style
            if (isBody && isCalcWidth(value) && isDesktopActive()) return
            if (isBody && isCalcWidth(value) && !hasVisibleOverlay()) return
            return origSet.call(this, value)
          },
          configurable: true
        })
      }
      if (prDesc && prDesc.set) {
        const origPRSet = prDesc.set
        Object.defineProperty(proto, 'paddingRight', {
          get: prDesc.get,
          set(value) {
            const isBody = this === document.body.style
            if (isBody && isCalcWidth(value) && isDesktopActive()) return
            return origPRSet.call(this, value)
          },
          configurable: true
        })
      }
      const origSetProperty = proto.setProperty
      if (origSetProperty) {
        proto.setProperty = function (prop, value, priority) {
          const isBody = this === document.body.style
          if (isBody && (prop === 'width' || prop === 'padding-right') && isCalcWidth(value) && isDesktopActive()) return
          return origSetProperty.call(this, prop, value, priority)
        }
      }
    }
  } catch {}
  // 实例级兜底：直接劫持 document.body.style（Chrome 上 width 不在原型时生效）
  const patchBodyStyleInstance = () => {
    try {
      const bodyStyle = document.body.style
      if (!bodyStyle || bodyStyle.__pcWidthPatched) return
      // 保存原始描述符（若原型上没有，则用 getPropertyValue/setProperty 实现）
      const origWidth = bodyStyle.width
      Object.defineProperty(bodyStyle, 'width', {
        get() {
          return this.getPropertyValue('width')
        },
        set(value) {
          if (isCalcWidth(value) && isDesktopActive()) return
          if (isCalcWidth(value) && !hasVisibleOverlay()) return
          // 使用 setProperty 以触发原型拦截的二次校验
          if (value === '' || value == null) this.removeProperty('width')
          else this.setProperty('width', value)
        },
        configurable: true
      })
      bodyStyle.__pcWidthPatched = true
    } catch {}
  }
  try {
    patchBodyStyleInstance()
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', patchBodyStyleInstance, { once: true })
    }
  } catch {}
  // 3. 让 getScrollBarSize 在桌面态返回 0（桌面无需补偿，否则表格固定列会预留 15px 白缝）
  try {
    const offDesc = Object.getOwnPropertyDescriptor(HTMLElement.prototype, 'offsetWidth')
    if (offDesc && offDesc.get) {
      const origGet = offDesc.get
      Object.defineProperty(HTMLElement.prototype, 'offsetWidth', {
        get() {
          try {
            if (isDesktopActive() && this.style && this.style.position === 'absolute' && this.style.width === '200px' && this.style.visibility === 'hidden') {
              return this.clientWidth
            }
          } catch {}
          return origGet.call(this)
        },
        configurable: true
      })
    }
  } catch {}
  // 4. 桌面态下固定列无需偏移，兜底清理 right 残留
  const cleanupFixedColumns = () => {
    if (!isDesktopActive()) return
    try {
      document.querySelectorAll('.ant-table-cell-fix-right[style*="right:"]').forEach((el) => {
        const r = el.style.right
        if (r && r !== '0px' && r !== '0') el.style.right = '0px'
      })
    } catch {}
  }
  try {
    const startObserver = () => {
      try {
        const obs = new MutationObserver(() => requestAnimationFrame(forceCleanup))
        obs.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] })
        obs.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] })
      } catch {}
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        startObserver()
        forceCleanup()
      }, { once: true })
    } else {
      startObserver()
      forceCleanup()
    }
    setInterval(forceCleanup, 800)
    window.addEventListener('resize', () => requestAnimationFrame(forceCleanup), { passive: true })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') requestAnimationFrame(forceCleanup)
    })
  } catch {}
  // 标记已加载，便于验证
  try {
    window.__fixBodyWidthLeakLoaded = true
  } catch {}
})()
import router from './router'
import App from './App.vue'
import './tailwind.css'
import { refreshDictTree } from './utils/dictCache'

let snowyPluginPromise

function loadSnowyPlugin(app) {
	if (!snowyPluginPromise) {
		snowyPluginPromise = import('./snowy')
			.then(({ default: snowy }) => app.use(snowy))
			.catch((error) => console.warn('加载扩展组件失败', error))
	}
	return snowyPluginPromise
}

function scheduleSnowyPlugin(app) {
	window.setTimeout(() => void loadSnowyPlugin(app), 1000)
}

async function bootstrap() {
	const app = createApp(App)
	app.use(createPinia())
	app.use(router)
	app.use(Antd)
	app.use(i18n)
	if (window.location.pathname !== '/index') {
		await loadSnowyPlugin(app)
	}

	// 挂载app
	app.mount('#app')

	// 首屏不依赖扩展组件和字典请求，避免后台慢时一直停在加载遮罩。
	scheduleSnowyPlugin(app)
	void refreshDictTree().catch((error) => console.warn('刷新系统字典失败', error))
}

bootstrap()
